<script lang="ts">
    
    import { onDestroy } from "svelte";
    import { Button, Icon } from "sveltestrap";
    import type { RealTimeEvent } from "../../global";
    import type { Board } from "../../Model/Board";
    import type { Game } from "../../Model/Game";
    import type { Player } from "../../Model/Player";
    import { boardsFactory, boardsService, eventsChannelsFactory, gamesFactory, playersFactory, playersService, roomsService } from "../../Services";
    import type { EventsChannel } from "../../Services/EventsChannel";
    import AspectRatio from "../Shared/AspectRatio.svelte";
    import If from "../Shared/If.svelte";
    import Picture from "../Shared/Picture.svelte";
    import Watch from "../Watch.svelte";
    import { i18n } from "../../Services/i18n";

    type PlayerItem = {
        player: Player;
        board?: Board;
    };

    export let roomId: string;

    let game: Game;
    let playerItems: PlayerItem[] = [];
    let roomEvents: EventsChannel;
    let containerWidth = 200;

    onDestroy(() => {
        roomEvents?.destroy();
    });

    $: getPlayers(roomId);

    async function getPlayers(roomId: string): Promise<void> {
        roomEvents?.destroy();
        const players = await roomsService.getPlayersInRoom(roomId);
        game = await roomsService.getCurrentGameAtRoom(roomId);
        playerItems = players.map(player => ({ player }));
        refreshBoards();
        roomEvents = eventsChannelsFactory.createEventsChannel(`room.${roomId}`);
        roomEvents.on('game_created', onGameCreated);
        roomEvents.on('player_enter', onPlayerEnter);
        roomEvents.on('player_leave', onPlayerLeave);
        roomEvents.on('board_created', onBoardCreated);
        roomEvents.on('number_checked', onNumberChecked);
        roomEvents.on('identity_override', onIdentityOverride);
    }

    async function refreshBoards(): Promise<void> {
        const boards = await boardsService.getBoardsOfCurrentGameInRoom(roomId);
        for (const playerItem of playerItems) {
            playerItem.board = boards.find(({ playerId }) => playerId === playerItem.player.id);
        }
        sortPlayerItems();
        playerItems = playerItems;
    }

    function hasPlayer(playerId: string): boolean {
        return playerItems.some(({ player }) => player.id === playerId)
    }

    function onGameCreated({ data }: RealTimeEvent): void {
        game = gamesFactory.createGameFromDto(data);
    }

    function onPlayerEnter({ data }: RealTimeEvent): void {
        if (hasPlayer(data.player.id)) {
            let updated = [];
            playerItems.forEach((info) => {
                if(info.player.id === data.player.id) {
                    info.player = playersFactory.createPlayerFromDto(data.player);
                }
                updated.push(info);
            });

            playerItems = updated;
            return;
        }
        
        const playerItem: PlayerItem = {
            player: playersFactory.createPlayerFromDto(data.player),
        };
        playerItems = [...playerItems, playerItem];

        sortPlayerItems();

        const playerSearch = playerItems.find((p: any) => p.player.id === data.player.id);

        if (!game || playerSearch) {
            console.log('No hay juego o el Jugador ya existe');
            return;
        }

        // boardsService.getBoardByGameAndPlayer(game.id, playerItem.player.id).then(board => {
        //     if (!board) {
        //         return;
        //     }
        //     playerItem.board = board;
        //     sortPlayerItems();
        //     playerItems = playerItems;
        // });
    }

    function onPlayerLeave({ data }: RealTimeEvent): void {
        if (!hasPlayer(data.player.id)) {
            return;
        }
        playerItems = playerItems.filter(({ player }) => player.id !== data.player.id);
    }

    function onBoardCreated({ data }: RealTimeEvent): void {
        const playerItem = playerItems.find(({ player }) => player.id === data.board.playerId);
        if (!playerItem) {
            return;
        }
        playerItem.board = boardsFactory.createBoardFromDto(data.board);
        sortPlayerItems();
        playerItems = playerItems;
    }

    function sortPlayerItems(): void {
        playerItems.sort((a, b) => {
            return (b.board?.checkedCount.value ?? 0) - (a.board?.checkedCount.value ?? 0);
        });
    }

    function onNumberChecked({ data }: RealTimeEvent): void {
        const playerItem = playerItems.find(({ board }) => board?.id === data.boardId);
        if (!playerItem) {
            return;
        }
        playerItem.board.checkCell(data.number);
        sortPlayerItems();
        playerItems = playerItems;
    }

    async function onOverridePlayerIdentity(player: Player): Promise<void> {
        if (!confirm($i18n('¿Seguro que deseas cambiar la identidad del jugador?'))) {
            return;
        }
        try {
            await playersService.overrideIdentity(player.id);
        } catch (error) {
            console.error(error);
        }
    }

    function onIdentityOverride({ data }: RealTimeEvent): void {
        const playerItem = playerItems.find(({ player }) => player.id === data.player.id);
        if (!playerItem) {
            return;
        }
        playerItem.player.name.set(data.player.name);
        playerItem.player.pictureId.set(data.player.pictureId);
        playerItem.player.identityOverride.set(true);
    }

</script>

<div class="players-ranking" bind:clientWidth={containerWidth}>
    {#each playerItems as { player, board }}
        <div class="player">
            <div class="picture">
                <AspectRatio>
                    <Picture id={player.pictureId} />
                </AspectRatio>
            </div>
            <div class="info">
                <div class="name" style="font-size: {containerWidth * 0.005}rem; padding-top: {containerWidth * 0.007}px;">
                    <Watch state={player.name} />
                </div>
                <If not state={player.identityOverride}>
                    <Button type="button" color="danger" size="sm" class="rounded-circle" on:click={() => onOverridePlayerIdentity(player)}>
                        <Icon name="person-x" />
                    </Button>
                </If>
                {#if board}
                    <div class="checked-count" style="font-size: {containerWidth * 0.005}rem;">
                        <Watch state={board.checkedCount} />/{board.numCells}
                    </div>
                {/if}
            </div>
        </div>
    {/each}
</div>

<style lang="scss">

    .player {
        width: 100%;
        display: flex;
        align-items: center;

        & + .player {
            margin-top: 0.5rem;
        }
    }

    .picture {
        position: relative;
        z-index: 2;
        width: 4rem;
        border-radius: 50%;
        overflow: hidden;
        border: 2px solid #fff;

        :global(img) {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
    }

    .info {
        flex: 1 1 0;
        box-sizing: border-box;
        margin-left: -3rem;
        padding-left: 4rem;
        padding-right: 1rem;
        display: flex;
        gap: 0.5rem;
        align-items: center;
        justify-content: space-between;
        background-color: rgba(255, 255, 255, 0.5);
        border-top-right-radius: 1rem;
        border-bottom-right-radius: 1rem;
    }

    .name {
        font-family: ManagerTitle;
        padding-top: 8px;
        box-sizing: border-box;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        min-width: 0;
        flex: 1 1 0;
    }

    .checked-count {
        font-family: ManagerTitle;
        white-space: nowrap;
    }

</style>
